/**
 * inspection 共有 URL の生成とトークン検証を担当するサービス
 */

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { normalizeUtcDateTimeString } from './session-time.util';
import type { InspectionAccessContext } from './inspection-access.store';

export interface InspectionLinkOption {
  tabId: string;
  tabName: string;
  url: string;
}

export interface InspectionLinkBundle {
  expiresAt: string;
  viewCd: number;
  selectedTabId: string;
  options: InspectionLinkOption[];
}

interface InspectionTokenValidationResult {
  expiresAt: string;
}

@Injectable({ providedIn: 'root' })
export class InspectionLinkService {
  private static readonly accessTokenQueryName = 'access_token';
  private static readonly checksumLength = 4;

  isInspectionRoute(pathname: string = window.location.pathname): boolean {
    return this.normalizePath(pathname) === this.normalizePath(environment.inspectionLink.path);
  }

  getAccessTokenQueryName(): string {
    return InspectionLinkService.accessTokenQueryName;
  }

  getAccessTokenFromUrl(search: string = window.location.search): string {
    return new URLSearchParams(search).get(InspectionLinkService.accessTokenQueryName)?.trim() ?? '';
  }

  getTabIdFromUrl(search: string = window.location.search): string {
    return new URLSearchParams(search).get('tabId')?.trim() ?? '';
  }

  getViewCdFromUrl(search: string = window.location.search): number {
    const rawViewCd = new URLSearchParams(search).get('viewcd');
    const parsed = Number(rawViewCd);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : environment.inspectionLink.defaultViewCd;
  }

  buildBundle(
    sessionKey: string,
    tabs: Array<{ id: string; name: string }>,
    preferredTabId: string | null,
  ): InspectionLinkBundle {
    const expiresAt = new Date(Date.now() + environment.inspectionLink.defaultValidityDays * 24 * 60 * 60 * 1000).toISOString();
    const accessToken = this.buildAccessToken(sessionKey, expiresAt);
    const selectedTabId = this.resolveSelectedTabId(tabs, preferredTabId);

    return {
      expiresAt,
      viewCd: environment.inspectionLink.defaultViewCd,
      selectedTabId,
      options: tabs.map(tab => ({
        tabId: tab.id,
        tabName: tab.name,
        url: this.buildInspectionUrl({
          sessionKey,
          tabId: tab.id,
          viewCd: environment.inspectionLink.defaultViewCd,
          accessToken,
          expiresAt,
        }),
      })),
    };
  }

  buildInspectionUrl(context: InspectionAccessContext, origin: string = window.location.origin): string {
    const url = new URL(this.normalizePath(environment.inspectionLink.path), origin);
    url.searchParams.set('viewcd', String(context.viewCd));
    url.searchParams.set('tabId', context.tabId);
    url.searchParams.set('session_key', context.sessionKey);
    url.searchParams.set(InspectionLinkService.accessTokenQueryName, context.accessToken);
    return url.toString();
  }

  buildContextFromUrl(sessionKey: string, tabId: string, viewCd: number, accessToken: string): InspectionAccessContext | null {
    const validation = this.validateAccessToken(sessionKey, accessToken);
    if (!validation) {
      return null;
    }

    return {
      sessionKey,
      tabId,
      viewCd,
      accessToken,
      expiresAt: validation.expiresAt,
    };
  }

  validateAccessToken(sessionKey: string, accessToken: string): InspectionTokenValidationResult | null {
    const normalizedToken = accessToken.trim();
    if (!normalizedToken || normalizedToken.length <= InspectionLinkService.checksumLength) {
      return null;
    }

    const encodedValue = normalizedToken.slice(0, -InspectionLinkService.checksumLength);
    const checksum = normalizedToken.slice(-InspectionLinkService.checksumLength);
    const obfuscatedExpirySeconds = Number.parseInt(encodedValue, 36);
    const encodedChecksum = Number.parseInt(checksum, 36);
    if (!Number.isFinite(obfuscatedExpirySeconds) || !Number.isFinite(encodedChecksum)) {
      return null;
    }

    const mask = this.buildTokenMask(sessionKey);
    const expiresAtSeconds = (obfuscatedExpirySeconds ^ mask) >>> 0;
    const expectedChecksum = this.buildChecksum(sessionKey, expiresAtSeconds);
    if (encodedChecksum !== expectedChecksum) {
      return null;
    }

    const expiresAt = normalizeUtcDateTimeString(new Date(expiresAtSeconds * 1000).toISOString());
    return expiresAt ? { expiresAt } : null;
  }

  isExpired(context: Pick<InspectionAccessContext, 'expiresAt'>): boolean {
    const normalizedExpiresAt = normalizeUtcDateTimeString(context.expiresAt);
    if (!normalizedExpiresAt) {
      return true;
    }

    return new Date(normalizedExpiresAt).getTime() <= Date.now();
  }

  ensureInspectionQueryParams(context: Pick<InspectionAccessContext, 'tabId' | 'viewCd'>): void {
    const currentUrl = new URL(window.location.href);
    let hasChanged = false;

    if (!currentUrl.searchParams.get('tabId') && context.tabId) {
      currentUrl.searchParams.set('tabId', context.tabId);
      hasChanged = true;
    }

    if (!currentUrl.searchParams.get('viewcd')) {
      currentUrl.searchParams.set('viewcd', String(context.viewCd));
      hasChanged = true;
    }

    if (!hasChanged) {
      return;
    }

    window.history.replaceState(window.history.state, document.title, `${currentUrl.pathname}?${currentUrl.searchParams.toString()}${currentUrl.hash}`);
  }

  cleanupSensitiveInspectionQueryParams(search: string = window.location.search): string {
    const params = new URLSearchParams(search);
    let hasRemoved = false;

    for (const queryName of ['session_key', 'sessionkey', InspectionLinkService.accessTokenQueryName]) {
      if (!params.has(queryName)) {
        continue;
      }

      params.delete(queryName);
      hasRemoved = true;
    }

    if (!hasRemoved) {
      return search;
    }

    const normalized = params.toString();
    return normalized ? `?${normalized}` : '';
  }

  private resolveSelectedTabId(tabs: Array<{ id: string }>, preferredTabId: string | null): string {
    if (preferredTabId && tabs.some(tab => tab.id === preferredTabId)) {
      return preferredTabId;
    }

    return tabs[0]?.id ?? '';
  }

  private buildAccessToken(sessionKey: string, expiresAt: string): string {
    const expiresAtSeconds = Math.floor(new Date(expiresAt).getTime() / 1000);
    const obfuscatedValue = ((expiresAtSeconds >>> 0) ^ this.buildTokenMask(sessionKey)) >>> 0;
    const checksum = this.buildChecksum(sessionKey, expiresAtSeconds)
      .toString(36)
      .padStart(InspectionLinkService.checksumLength, '0');
    return `${obfuscatedValue.toString(36)}${checksum}`;
  }

  private buildTokenMask(sessionKey: string): number {
    return this.hash(`${sessionKey}:${environment.inspectionLink.tokenSalt}`) & 0x7fffffff;
  }

  private buildChecksum(sessionKey: string, expiresAtSeconds: number): number {
    return this.hash(`${sessionKey}:${expiresAtSeconds}:${environment.inspectionLink.tokenSalt}`) % (36 ** InspectionLinkService.checksumLength);
  }

  private hash(value: string): number {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return hash >>> 0;
  }

  private normalizePath(pathname: string): string {
    if (!pathname) {
      return '/';
    }

    const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
    return normalized.length > 1 && normalized.endsWith('/')
      ? normalized.slice(0, -1)
      : normalized;
  }
}
